import { Navigate, Outlet, RouteObject, useNavigate } from "react-router";

import PageError from "@/shared/ui/errors/PageError";
import * as Sentry from "@sentry/react";

export const TAB_INDEX_TO_PATH: Record<number, string> = {
	0: "organization",
	1: "satos-apps",
	2: "procedures",
	3: "competence",
	4: "shift",
	5: "task",
	6: "lab",
	7: "material-handling",
	8: "training",
	9: "language",
	10: "server",
	11: "system-information",
};

export const SettingsRoutes: RouteObject = {
	path: "settings",
	lazy: async () => {
		const { SettingsHome } =
			await import("@/app/application/admin-menu/settings/SettingsHome");
		return {
			Component: () => (
				<Sentry.ErrorBoundary
					fallback={(boundary) => (
						<PageError
							error={boundary.error}
							componentStack={boundary.componentStack}
							reset={() => boundary.resetError()}
						/>
					)}
				>
					<SettingsHome />
				</Sentry.ErrorBoundary>
			),
		};
	},
	children: [
		{
			index: true,
			element: <Navigate to="organization" replace />,
		},
		{
			path: "organization",
			lazy: async () => {
				const mod =
					await import("@/app/application/admin-menu/settings/OrganizationSettings");
				return { Component: mod.default };
			},
		},
		{
			path: "satos-apps",
			lazy: async () => {
				const mod =
					await import("@/app/application/admin-menu/settings/ActiveModules");
				return { Component: mod.default };
			},
		},
		{
			path: "procedures",
			lazy: async () => {
				const mod =
					await import("@/app/application/admin-menu/settings/ProceduresSettings");
				return { Component: mod.default };
			},
		},
		{
			path: "competence",
			lazy: async () => {
				const mod =
					await import("@/app/application/admin-menu/settings/CompetencySettings");
				return { Component: mod.default };
			},
		},
		{
			path: "shift",
			lazy: async () => {
				const mod =
					await import("@/app/application/admin-menu/settings/ShiftSettingsPage");
				return { Component: mod.default };
			},
		},
		{
			path: "task",
			lazy: async () => {
				const mod =
					await import("@/app/application/admin-menu/settings/TaskSettings");
				return { Component: mod.default };
			},
		},
		{
			path: "lab",
			lazy: async () => {
				const mod =
					await import("@/app/application/admin-menu/settings/LabSettings");
				return { Component: mod.default };
			},
		},
		{
			path: "material-handling",
			lazy: async () => {
				const { MaterialHandlingSettingsPage } =
					await import("@/app/application/admin-menu/settings/MaterialHandlingSettingsPage");
				return { Component: MaterialHandlingSettingsPage };
			},
		},
		{
			path: "training",
			lazy: async () => {
				const mod =
					await import("@/app/application/admin-menu/settings/TrainingSettingsPage");
				return { Component: mod.default };
			},
		},
		{
			path: "language",
			element: <Outlet />,
			children: [
				{
					index: true,
					lazy: async () => {
						const { LanguageSettings } =
							await import("@/app/application/admin-menu/settings/language-settings/LanguageSettings");
						return { Component: LanguageSettings };
					},
				},
				{
					path: "create-new",
					lazy: async () => {
						const { CreateNewLanguageSetting } =
							await import("@/app/application/admin-menu/settings/language-settings/CreateNewLanguageSetting");
						return {
							Component: () => {
								const navigate = useNavigate();
								return (
									<CreateNewLanguageSetting
										handleClose={() => navigate("/settings/language")}
									/>
								);
							},
						};
					},
				},
			],
		},
		{
			path: "server",
			lazy: async () => {
				const mod =
					await import("@/app/application/admin-menu/settings/ServerSettings");
				return { Component: mod.default };
			},
		},
		{
			path: "system-information",
			lazy: async () => {
				const mod =
					await import("@/app/application/admin-menu/settings/SystemInformation");
				return { Component: mod.default };
			},
		},
	],
};
